import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Language {
  name: string;
  value: string;
}

export const languages: Language[] = [
  {
    name: 'Deutsch',
    value: 'de-DE',
  },
  {
    name: 'English',
    value: 'en-US',
  },
  {
    name: 'Français',
    value: 'fr-FR',
  },
];

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private translate = inject(TranslocoService);

  private LANGUAGE_STORAGE_KEY = 'CT_LANGUAGE';
  private NAVIGATOR_LANGUAGE_VALUE: string = navigator?.language ?? 'en-US';
  private STANDARD_LANGUAGE_VALUE: string =
    languages.find(language => language.value === this.NAVIGATOR_LANGUAGE_VALUE)
      ?.value ??
    languages.find(
      language =>
        language.value ===
        `${this.NAVIGATOR_LANGUAGE_VALUE.toLocaleLowerCase()}-${this.NAVIGATOR_LANGUAGE_VALUE.toLocaleUpperCase()}`
    )?.value ??
    'en-US';
  currentLanguage!: Language;
  lsAvailable = false;
  private languageChange: BehaviorSubject<any> = new BehaviorSubject(null);
  languageChange$: Observable<any> = this.languageChange.asObservable();

  constructor() {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      this.lsAvailable = true;
    } catch (e) {
      this.lsAvailable = false;
    }

    this.setLanguage(this.read());
  }

  setLanguage(value: string) {
    return new Promise(resolve => {
      this.currentLanguage = this.getLanguage(value);
      this.translate.setActiveLang(this.currentLanguage.value);
      this.write(this.currentLanguage.value);
      this.languageChange.next(null);
      resolve(this.currentLanguage);
    });
  }

  getLanguage(value: string): Language {
    return (
      languages.find(language => language.value === value) ||
      languages.find(
        language => language.value === this.STANDARD_LANGUAGE_VALUE
      ) ||
      languages[0]
    );
  }

  private read(): string {
    if (!this.lsAvailable) return this.STANDARD_LANGUAGE_VALUE;
    return (
      localStorage.getItem(this.LANGUAGE_STORAGE_KEY) ??
      this.STANDARD_LANGUAGE_VALUE
    );
  }

  private write(value: string) {
    if (!this.lsAvailable) return;
    localStorage.setItem(this.LANGUAGE_STORAGE_KEY, value);
  }

  getTranslateService() {
    return this.translate;
  }

  getLanguageForRequest(allowFrFromBrowser = false): string {
    const fromService = this.currentLanguage?.value;
    if (fromService) return fromService;

    if (this.lsAvailable) {
      const fromStorage = localStorage.getItem(this.LANGUAGE_STORAGE_KEY);
      if (fromStorage) return fromStorage;
    }

    const navigatorLang = (navigator?.language ?? '').toLowerCase();
    if (!navigatorLang) return 'de-DE';

    const allowed = allowFrFromBrowser
      ? languages
      : languages.filter(l => l.value !== 'fr-FR');
    const match =
      allowed.find(l => l.value.toLowerCase() === navigatorLang) ??
      allowed.find(l => l.value.toLowerCase().startsWith(navigatorLang.slice(0, 2)));
    if (match) return match.value;

    return 'de-DE';
  }
}
